import {
  Color,
  ShaderMaterial,
  Vector2,
  Vector4,
  VideoTexture,
  BaseEvent,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  TextureLoader,
  Vector3,
  Texture,
  Uniform,
  RGBAFormat,
  Clock,
} from 'three';
import { FitMode, Palette, SphereItemType } from 'shared/interfaces/planogram';
import type { BaseObjectUserData } from 'shared/object3d/interfaces';
import BaseObject3D from 'shared/object3d/BaseObject3D';
import { VideoObjectConfig } from 'shared/interfaces/object_config';
import { generateThumbnailUrl, THUMBNAIL_SIZE } from 'shared/utils/url';
import { fitClusterChild } from 'shared/utils/Packing.util';
import { packShading } from 'shared/utils/format';

import scaledVertexShader from 'shared/renderingEngine/shaders/ScaledVertexShader.glsl';
import scaledFragmentShader from 'shared/renderingEngine/shaders/ScaledFragmentShader.glsl';
import videoFragmentShader from 'shared/renderingEngine/shaders/VideoFragmentShader.glsl';
import standardVertexShader from 'shared/renderingEngine/shaders/StandardVertexShader.glsl';
import preloaderFragmentShader from 'shared/renderingEngine/shaders/VideoPreloaderFragmentShader.glsl';

import playIcon from 'shared/assets/icons/video_play.png';
import pauseIcon from 'shared/assets/icons/video_pause.png';
import shareIcon from 'shared/assets/icons/video_share.png';
import { resolvePaletteColor } from '../utils/colors';
import CanceledError from '../errors/Canceled';

const planeGeometry = new PlaneGeometry(1, 1, 1, 1);

const PLAY_SIZE = 0.2;
const PAUSE_SIZE = 0.2;
const SHARE_SIZE = 0.15;

export const DEFAULT_SENSITIVITY: [number, number] = [0.05, 0.05];

const textureLoader = new TextureLoader(); // TODO: use a loading manager to inform the loading screens?

function createButtonMaterial(icon: string) {
  const texture = textureLoader.load(icon);
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;

  return new MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity: 0.8,
    depthTest: false,
  });
}

const playButtonMaterial = createButtonMaterial(playIcon);
const pauseButtonMaterial = createButtonMaterial(pauseIcon);
const shareButtonMaterial = createButtonMaterial(shareIcon);

interface VideoHoverEvent extends BaseEvent {
  type: 'VideoHover';
  hovered: boolean;
}

type ControlsEvents = VideoHoverEvent;

class VideoControls3D extends Object3D<ControlsEvents> {
  type: 'Group';
  readonly isGroup: true;

  private playButton: Mesh;
  private pauseButton: Mesh;
  private shareButton: Mesh;

  private hovered: boolean = false;
  private playing: boolean = false;
  private share: boolean = false;

  constructor() {
    super();
    this.name = 'VideoControls';
    this.playButton = new Mesh(planeGeometry, playButtonMaterial);
    this.playButton.name = 'VideoPlayButton';

    this.pauseButton = new Mesh(planeGeometry, pauseButtonMaterial);
    this.pauseButton.name = 'VideoPauseButton';

    this.shareButton = new Mesh(planeGeometry, shareButtonMaterial);
    this.shareButton.name = 'VideoShareButton';

    this.add(this.playButton, this.pauseButton, this.shareButton);

    this.updateVisibility();

    const playPauseHandler = () => {
      this.parent?.dispatchEvent({ type: 'VideoPlayPause' });
    };
    this.playButton.userData.pointerdown = playPauseHandler;
    this.pauseButton.userData.pointerdown = playPauseHandler;

    this.shareButton.userData.pointerdown = () => {
      this.parent?.dispatchEvent({ type: 'VideoShare' });
    };

    this.addEventListener('VideoHover', (event: VideoHoverEvent) => {
      if (this.hovered !== event.hovered) {
        this.hovered = event.hovered;
        this.updateVisibility();
      }
    });

    // TODO: update only one of the buttons each time?
    this.playButton.onBeforeRender = () => this.updateScale();
    this.pauseButton.onBeforeRender = () => this.updateScale();
    this.shareButton.onBeforeRender = () => this.updateScale();
  }

  configure(config: VideoObjectConfig) {
    const configShare = config.share ?? false;
    if (this.share !== configShare) {
      this.share = configShare;
      this.updateVisibility();
    }
  }

  updateScale() {
    if (this.parent === null) return;
    const parentScale = this.parent.scale;
    const smallerSide = Math.min(parentScale.x, parentScale.y);
    const scaleVector = new Vector3();
    scaleVector.set(smallerSide / parentScale.x, smallerSide / parentScale.y, 1);
    this.playButton.scale.copy(scaleVector).multiplyScalar(PLAY_SIZE);
    this.pauseButton.scale.copy(scaleVector).multiplyScalar(PAUSE_SIZE);
    this.shareButton.scale.copy(scaleVector).multiplyScalar(SHARE_SIZE);
    this.shareButton.position
      .set(1, 1, 1)
      .sub(this.shareButton.scale)
      .subScalar(0.03)
      .multiplyScalar(0.5);
  }

  setPlaying(playing: boolean) {
    if (this.playing !== playing) {
      this.playing = playing;
      this.updateVisibility();
    }
  }

  private updateVisibility() {
    this.playButton.visible = !this.playing;
    this.pauseButton.visible = this.hovered && this.playing;
    this.shareButton.visible = this.share;
  }
}

class VideoPoster3D extends Object3D<ControlsEvents> {
  private mesh: Mesh;
  private uniforms: Record<string, Uniform>;
  private texture: Texture | undefined;
  private textureUrl: string | undefined;

  constructor() {
    super();

    this.name = 'VideoPoster';

    this.uniforms = {
      imageTexture: new Uniform(undefined),
      uvScale: new Uniform(new Vector2(1.0, 1.0)),
      uvOffset: new Uniform(new Vector2(0, 0)),
      backgroundColor: new Uniform(new Vector4(0.0, 0.0, 0.0, 0.0)),
      cornerRadius: new Uniform(new Vector4(0, 0, 0, 0)),
      canvasUnitSize: new Uniform(new Vector2(1, 1)),
      shading: new Uniform(new Vector4(1, 1, 1, 1)),
    };

    const material = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: scaledVertexShader,
      fragmentShader: scaledFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      defines: {
        CORNER_RADIUS: '',
        SHADING: '',
      },
    });

    this.mesh = new Mesh(planeGeometry, material);
    this.mesh.name = 'VideoPosterMesh';
    this.add(this.mesh);
  }

  private textureLoading: Promise<void> = Promise.resolve();

  update(config: VideoObjectConfig, palette?: Palette) {
    this.mesh.visible = config.poster !== undefined;
    if (config.poster === undefined) {
      this.dispose();
      return;
    }
    this.uniforms.cornerRadius.value = new Vector4(...(config.cornerRadius ?? [0, 0, 0, 0]));
    this.uniforms.shading.value = packShading(config.shading, config.opacity, palette);
    // TODO: render posters using the LOD system
    const url =
      config.poster.thumbnails === undefined
        ? config.poster.url
        : generateThumbnailUrl(
            config.poster.thumbnails,
            config.autoplay ? THUMBNAIL_SIZE.Small : THUMBNAIL_SIZE.Large,
          );
    if (this.textureUrl !== url) {
      this.textureUrl = url;
      this.textureLoading = textureLoader.loadAsync(url).then(texture => {
        // prevent slow texture loads overriding new textures
        if (url !== this.textureUrl) return;
        this.uniforms.imageTexture.value = texture;
        this.texture = texture;
      });
    }
    this.textureLoading.then(() => {
      this.updateScale(new Vector2(config.scale[0], config.scale[1]), config);
    });
  }

  // TODO: poke on selectionEventManager.on(SelectionEvents.SCALE_CHANGE
  private updateScale(scale: Vector2, config: VideoObjectConfig) {
    const poster = config.poster;
    const texture = this.texture;
    if (texture === undefined || poster === undefined) return;
    const fitted = fitClusterChild(
      new Vector2(
        poster.naturalWidth ?? texture.image?.width ?? 1,
        poster.naturalHeight ?? texture.image?.height ?? 1,
      ),
      scale,
      FitMode.Fill, // TODO: account for cropping?
    );
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.format = RGBAFormat;

    const uvScale =
      fitted.cropRatio ??
      (config.cropRatio ? new Vector2(...config.cropRatio) : undefined) ??
      new Vector2(1, 1);
    const uvOffset =
      fitted.cropOffset ??
      (config.cropOffset ? new Vector2(...config.cropOffset) : undefined) ??
      new Vector2(0, 0);
    this.uniforms.uvScale.value = uvScale;
    this.uniforms.uvOffset.value = uvOffset;
    this.uniforms.imageTexture.value = texture;
  }

  dispose() {
    this.uniforms.imageTexture.value?.dispose();
    this.uniforms.imageTexture.value = undefined;
  }
}

const clock = new Clock();

class VideoPreloader3D extends Object3D {
  private uniforms: Record<string, Uniform>;
  constructor() {
    super();
    this.name = 'VideoPreloader';

    this.uniforms = {
      time: new Uniform(0),
      aspectRatio: new Uniform(1.0),
      color: new Uniform(new Color(0xffffff)),
      scale: new Uniform(0.2),
    };

    const material = new ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: standardVertexShader,
      fragmentShader: preloaderFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      defines: {
        PI: Math.PI,
      },
    });

    const mesh = new Mesh(planeGeometry, material);
    mesh.name = 'VideoPreloaderMesh';

    mesh.onBeforeRender = () => {
      this.uniforms.time.value = clock.getElapsedTime();
      this.updateScale();
    };

    this.add(mesh);
  }

  updateScale() {
    if (this.parent === null) return;
    this.uniforms.aspectRatio.value = this.parent.scale.x / this.parent.scale.y;
  }
}

interface VideoUserData extends BaseObjectUserData {
  pointerover: () => void;
  pointerout: () => void;
}

const resolveRedirects = (url: string) => {
  if (url === '') return '';
  else return fetch(url, { method: 'HEAD' }).then(result => result.url);
};

export default class VideoObject3D extends BaseObject3D<VideoUserData> {
  private readonly controls: VideoControls3D;
  private readonly poster: VideoPoster3D;
  private readonly placeholder: Mesh<PlaneGeometry, MeshBasicMaterial>;
  private readonly preloader: VideoPreloader3D;
  private readonly videoMesh: Mesh<PlaneGeometry, ShaderMaterial>;
  private readonly videoElement: HTMLVideoElement;

  private rememberedPlayTime: number = 0;
  private stutteredTime: number = -1;

  private playing: boolean = false;
  private loaded: boolean = false;

  get playTime(): number {
    return this.videoElement.currentTime;
  }

  private config: VideoObjectConfig | undefined;
  private palette: Palette | undefined;

  constructor() {
    super();

    this.name = 'Video';

    this.itemType = SphereItemType.Video;
    this.videoElement = this.createVideoElement();

    this.placeholder = new Mesh(
      planeGeometry,
      new MeshBasicMaterial({
        color: 'red',
        transparent: true,
        depthTest: false,
      }),
    );
    this.placeholder.name = 'VideoPlaceholderMesh';
    this.add(this.placeholder);

    const videoTexture = new VideoTexture(this.videoElement);
    const videoMaterial = new ShaderMaterial({
      uniforms: {
        imageTexture: { value: videoTexture },
        maskTexture: { value: undefined },
        maskColor: { value: new Color('#000000') },
        maskZero: { value: DEFAULT_SENSITIVITY[0] },
        maskOne: { value: DEFAULT_SENSITIVITY[1] },
        cornerRadius: { value: new Vector4(0, 0, 0, 0) },
        canvasUnitSize: { value: new Vector2(1, 1) },
        uvOffset: { value: new Vector2(0, 0) },
        uvScale: { value: new Vector2(1, 1) },
        shading: { value: new Vector4(1, 1, 1, 1) },
      },
      defines: {},
      vertexShader: scaledVertexShader,
      fragmentShader: videoFragmentShader,
      transparent: true,
      depthTest: false,
    });
    this.videoMesh = new Mesh(planeGeometry, videoMaterial);
    this.videoMesh.name = 'VideoMesh';
    this.add(this.videoMesh);

    this.poster = new VideoPoster3D();
    this.add(this.poster);

    this.preloader = new VideoPreloader3D();
    this.add(this.preloader);

    this.controls = new VideoControls3D();
    this.add(this.controls);
    this.controls.updateScale();

    this.userData.pointerover = () => {
      this.controls.dispatchEvent({ type: 'VideoHover', hovered: true });
    };
    this.userData.pointerout = () => {
      this.controls.dispatchEvent({ type: 'VideoHover', hovered: false });
    };

    this.addEventListener('VideoPlayPause', () => {
      if (this.config?.hideControls === false) this.setPlaying(!this.isPlaying());
    });

    this.updateVisibility();
  }

  setPlaceholderColor(color: Color, opacity: number) {
    this.placeholder.material.color = color;
    this.placeholder.material.opacity = opacity;
  }

  private createVideoElement() {
    const videoElement = document.createElement('video');
    videoElement.id = this.uuid;
    videoElement.crossOrigin = 'anonymous';
    videoElement.preload = 'metadata';
    videoElement.loop = false;
    videoElement.autoplay = false;
    videoElement.setAttribute('playsinline', ''); // otherwise iOS Safari starts fullscreen playback

    videoElement.onwaiting = () => {
      this.stutteredTime = videoElement.currentTime;
    };

    return videoElement;
  }

  private preloading: Promise<boolean> | undefined;
  private cancelPreloading: (() => void) | undefined;

  preloadVideo(): Promise<boolean> {
    if (this.preloading !== undefined) return this.preloading;

    let canceled: boolean = false;
    this.preloading = new Promise<boolean>(async (resolve, reject) => {
      // resolve any redirects to prevent CORS in Safari (e. g. for Vimeo hosted videos)
      const url = await resolveRedirects(this.url);
      this.videoElement.firstChild?.remove();
      if (url === '') {
        resolve(false);
        return;
      }
      const source = document.createElement('source', {});
      source.src = url;
      this.videoElement.appendChild(source);
      this.videoElement.load();
      if (canceled) return;
      this.videoElement.onerror = reject; // TODO: throw an actual Error

      const loaded = async () => {
        if (this.loaded) return;
        if (canceled || url !== (this.videoElement.firstChild as HTMLSourceElement | null)?.src)
          reject(new CanceledError());
        // force the first few seconds of the video to load
        // preload = auto only loads metadata on slow connections
        // we can't access the thumbnail from the metadata
        this.videoElement.currentTime = this.rememberedPlayTime;
        this.loaded = true;
        this.updateVisibility();
        resolve(true);
      };
      this.videoElement.onloadedmetadata = loaded;
      this.videoElement.onloadeddata = loaded;
    });
    this.cancelPreloading = () => {
      canceled = true;
    };
    return this.preloading;
  }

  unloadVideo() {
    if (this.cancelPreloading !== undefined) {
      this.cancelPreloading();
      this.cancelPreloading = undefined;
    }
    this.preloading = undefined;
    this.setPlaying(false);
    if (this.loaded) this.rememberedPlayTime = this.videoElement.currentTime;
    this.loaded = false;
    this.videoElement.onloadeddata = null;
    this.videoElement.onloadedmetadata = null;
    this.videoElement.onerror = null;
    this.videoElement.firstChild?.remove();
    this.videoElement.load();
    this.updateVisibility();
  }

  private url: string = '';

  private maskImageUrl: string = '';

  configure(config: VideoObjectConfig, palette?: Palette) {
    this.poster.update(config, palette);
    this.controls.configure(config);

    this.videoElement.loop = config.loop;
    this.videoElement.muted = config.autoplay;

    const override = this.config === undefined;

    const paletteDiff = this.palette !== palette;

    const diff = (key: keyof VideoObjectConfig) => this.config?.[key] !== config[key];

    if (override || diff('videoUrl')) {
      this.disposeVideo();
      this.url = config.videoUrl;
      const wasLoaded = this.loaded;
      this.unloadVideo();
      if (config.videoUrl !== undefined && config.videoUrl.length > 0 && wasLoaded) {
        this.preloadVideo();
      }
    }

    if (override || diff('cornerRadius')) {
      this.videoMesh.material.uniforms.cornerRadius.value = new Vector4(
        ...(config.cornerRadius ?? [0, 0, 0, 0]),
      );
    }

    if (override || diff('cropOffset') || diff('cropRatio')) {
      const offset = new Vector2(0, 0);
      if (config.cropOffset !== undefined) offset.set(...config.cropOffset);
      const ratio = new Vector2(1, 1);
      if (config.cropRatio !== undefined) ratio.set(...config.cropRatio);
      this.videoMesh.material.uniforms.uvOffset.value = offset;
      this.videoMesh.material.uniforms.uvScale.value = ratio;
    }

    if (override || diff('scale')) this.updateScale();

    if (override || paletteDiff || diff('maskColor')) {
      if (config.maskColor === undefined) this.setMaskColor(undefined);
      else this.setMaskColor(resolvePaletteColor(config.maskColor, palette));
    }

    if (override || diff('maskImage')) {
      const material = this.videoMesh.material;
      const wasEnabled = material.defines.MASK_IMAGE;
      if (config.maskImage === undefined) {
        delete material.defines.MASK_IMAGE;
        material.uniforms.maskTexture.value?.dispose();
        material.uniforms.maskTexture.value = undefined;
        this.maskImageUrl = '';
      } else {
        material.defines.MASK_IMAGE = '';
        const url = generateThumbnailUrl(config.maskImage.thumbnails);
        if (this.maskImageUrl !== url) {
          this.maskImageUrl = url;
          textureLoader.loadAsync(url).then(texture => {
            // prevent slow texture loads overriding new textures
            if (url !== this.maskImageUrl) return;
            material.uniforms.maskTexture.value = texture;
          });
        }
      }
      if (wasEnabled !== material.defines.MASK_IMAGE) material.needsUpdate = true;
    }

    if (override || diff('maskSensitivity')) {
      if (config.maskSensitivity !== undefined) this.setMaskSensitivity(config.maskSensitivity);
    }

    if (override || paletteDiff || diff('shading') || diff('opacity'))
      this.videoMesh.material.uniforms.shading.value = packShading(
        config.shading,
        config.opacity,
        palette,
      );

    this.config = config;
    this.palette = palette;
    this.updateVisibility();
  }

  private updateVisibility() {
    const posterAvailable = this.config?.poster !== undefined;
    this.poster.visible = posterAvailable && !this.playing;
    this.preloader.visible = !this.loaded && this.videoElement.firstChild !== null;
    this.videoMesh.visible = this.loaded && !this.poster.visible;
    this.placeholder.visible = !this.poster.visible && !this.videoMesh.visible;
  }

  private setMaskColor(color: string | undefined) {
    const masking = color !== undefined;
    const material = this.videoMesh.material;
    delete material.defines.MASK_COLOR;
    if (masking) {
      material.defines.MASK_COLOR = '';
      const maskColor = new Color(color);
      material.uniforms.maskColor.value = maskColor;
    }
    material.needsUpdate = true;
  }

  private setMaskSensitivity(sensitivity: [number, number]) {
    const material = this.videoMesh.material;
    material.uniforms.maskZero.value = sensitivity[0];
    material.uniforms.maskOne.value = sensitivity[1];
    material.needsUpdate = true;
  }

  isPlaying() {
    return this.playing;
  }

  async setPlaying(playing: boolean): Promise<void> {
    if (this.playing === playing) return;
    this.playing = playing;
    this.controls.setPlaying(playing);
    this.updateVisibility();

    if (playing) {
      const resetToPause = () => {
        this.playing = false;
        this.controls.setPlaying(this.playing);
        this.updateVisibility();
      };
      const preloading = await this.preloadVideo();
      if (!preloading) {
        resetToPause();
      } else {
        try {
          return await this.videoElement.play();
        } catch (e) {
          resetToPause();
          throw e;
        }
      }
    } else {
      this.videoElement.pause();
    }
  }

  isStuttering(): boolean {
    return (this.videoElement.currentTime ?? 0) <= this.stutteredTime;
  }

  hideControls(hide: boolean) {
    this.controls.visible = !hide;
  }

  updateScale() {
    this.videoMesh.material.uniforms.canvasUnitSize.value = new Vector2(this.scale.x, this.scale.y);
    this.controls.updateScale();
  }

  private disposeVideo() {
    const videoElm = this.videoElement;
    videoElm.pause();
    videoElm.removeAttribute('src');
    videoElm.load();
  }

  dispose() {
    this.disposeVideo();
    this.videoMesh.material.dispose();
    this.videoMesh.material.uniforms.imageTexture.value.dispose();
    this.videoMesh.material.uniforms.maskTexture.value?.dispose();
  }
}
